import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, race } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { ConnectionStatusService } from 'src/app/services/connection-status.service';
import { EMPTY_OBJECT } from 'src/globals/globals';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class EmployeeTourMapDirectionsCalculationService {
  private directionsServiceSubject: BehaviorSubject<
    google.maps.DirectionsService | {}
  >;
  private directionsServiceIsInitialized: Observable<
    google.maps.DirectionsService | {}
  >;

  private readonly BASE_EXPONENTIAL_BACKOFF_MS = 100;

  private readonly MAX_RETRIES = 25;

  private readonly EMPTY_RESULT: google.maps.DirectionsResult = {
    geocoded_waypoints: [],
    routes: []
  };

  constructor(private connectionStatusService: ConnectionStatusService) {
    this.directionsServiceSubject = new BehaviorSubject<
      google.maps.DirectionsService | {}
    >(EMPTY_OBJECT);
    this.directionsServiceIsInitialized = this.directionsServiceSubject.pipe(
      filter(s => s != null && s !== EMPTY_OBJECT)
    );
    this.connectionStatusService
      .getConnectionStatusSubject()
      .pipe(takeUntil(this.directionsServiceIsInitialized))
      .subscribe((connected: boolean) => {
        if (connected) {
          try {
            this.directionsServiceSubject.next(
              new google.maps.DirectionsService()
            );
          } catch (err) {
            console.error(err);
          }
        }
      });
  }

  public calculateDirections(
    markers: CoordinateLike[],
    cancelObservable: Observable<any>
  ): Promise<google.maps.DirectionsResult> {
    if (!CollectionHelper.isPopulated(markers)) {
      return Promise.resolve(this.EMPTY_RESULT);
    }
    const baseObservable =
      cancelObservable == null
        ? this.directionsServiceIsInitialized
        : race(this.directionsServiceIsInitialized, cancelObservable);
    const waitObservable = baseObservable.pipe(take(1));
    return new Promise((resolve, reject) => {
      waitObservable.subscribe(
        (directionsService: google.maps.DirectionsService) => {
          if (directionsService == null || directionsService === EMPTY_OBJECT) {
            return resolve(this.EMPTY_RESULT);
          } else {
            this.doExponentialBackoff(
              markers,
              directionsService,
              cancelObservable,
              resolve,
              reject
            );
          }
        }
      );
    });
  }

  private async doExponentialBackoff(
    markers: CoordinateLike[],
    directionsService: google.maps.DirectionsService,
    cancelObservable: Observable<any>,
    resolve: (
      value:
        | google.maps.DirectionsResult
        | PromiseLike<google.maps.DirectionsResult>
    ) => void,
    reject: (reason?: any) => void
  ): Promise<void> {
    let cancelled = false;
    let numRetries = 0;
    cancelObservable
      .pipe(take(1))
      .toPromise()
      .then(() => (cancelled = true));
    let exponentialBackoffCounter = 0;
    const requestBody: RequestBody = this.constructDirectionsRequestBody(
      markers
    );
    while (!cancelled && numRetries < this.MAX_RETRIES) {
      try {
        const result = await this.doCalculateDirections(
          requestBody,
          directionsService
        );
        return resolve(result);
      } catch (err) {
        if (err === google.maps.DirectionsStatus.OVER_QUERY_LIMIT) {
          numRetries++;
          const upperBound = exponentialBackoffCounter;
          exponentialBackoffCounter = Math.min(
            exponentialBackoffCounter + 1,
            5
          );
          await GeneralHelper.sleep(
            _.random(0, Math.pow(2, upperBound)) *
              this.BASE_EXPONENTIAL_BACKOFF_MS
          );
        } else {
          return reject(err);
        }
      }
    }
    return resolve(this.EMPTY_RESULT);
  }

  private async doCalculateDirections(
    requestBody: RequestBody,
    directionsService: google.maps.DirectionsService
  ): Promise<google.maps.DirectionsResult> {
    return new Promise((resolve, reject) => {
      directionsService.route(
        requestBody,
        (result: google.maps.DirectionsResult, status) => {
          if (status === google.maps.DirectionsStatus.OVER_QUERY_LIMIT) {
            reject(google.maps.DirectionsStatus.OVER_QUERY_LIMIT);
          } else if (status !== google.maps.DirectionsStatus.OK) {
            reject(new Error(`Status not OK: ${status}`));
          } else {
            resolve(result);
          }
        }
      );
    });
  }

  private constructDirectionsRequestBody(
    markers: CoordinateLike[]
  ): RequestBody {
    const start = markers[0];
    const end = markers[markers.length - 1];
    return {
      origin: {
        lat: start.lat,
        lng: start.lng
      },
      destination: {
        lat: end.lat,
        lng: end.lng
      },
      waypoints: markers.map(m => {
        return {
          location: new google.maps.LatLng(m.lat, m.lng),
          stopover: true // setting this to true ensures a route leg actually stops at this waypoint
        };
      }),
      optimizeWaypoints: false, // setting this to false preserves the waypoint order
      travelMode: google.maps.TravelMode.DRIVING
    };
  }
}

export interface CoordinateLike {
  lat: number;
  lng: number;
}

interface RequestBody {
  origin: google.maps.LatLngLiteral;
  destination: google.maps.LatLngLiteral;
  waypoints: google.maps.DirectionsWaypoint[];
  optimizeWaypoints: boolean;
  travelMode: google.maps.TravelMode;
}
